/// <reference path="../interface.d.ts" />
import { Button, Form, message, Segmented, Tag, Tooltip } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { handleLinkedStockDependentsError } from "components/ManageProduct/LinkedStockDependentsModal";
import SkuModalLinkedStockTab, { LOCAL_STOCK_REF_PREFIX, type SkuLocalStockDraft } from "./SkuModalLinkedStockTab";
import type { ProductStockPickerRow } from "services/interface/productStockPicker.interface";
import { useSkuModalStockEditOptional } from "./SkuModalStockEditContext";
import {
  normalizeLinkedQuantity,
  type SkuLinkedStockDraftRow,
} from "services/interface/productLinkedStock.interface";
import styled from "@emotion/styled";
import VariationOptionCard from "./SKUMainCard";
import { v4 as uuidv4 } from "uuid";
import { isEmpty } from "lodash"; // ✅ bundle-barrel-imports: import เฉพาะที่ใช้แทน import ทั้ง lodash
import { backendUrl } from "config";
import { useAppSelector } from "store";
import { toMerchantIdArray } from "utils";
import { useGetFactoryById } from "hooks/useFactory";
import { useUpdateProductSku } from "hooks/useProduct";
import { ProductVariantDto, UpdateProductSkuDto } from "services/interface";
import { parsePrice, buildFormattedPrice, buildSingleItemDto } from "./helpers";
import { ENABLE_ADVANCED_SELF_STOCK } from "./skuModalConstants";
import { STOCK_MODE_ADVANCED_LABEL } from "components/ManageProduct/Recipe/bundleLabels";
import type {
  LinkedStockUiMode,
} from "services/interface/productLinkedStock.interface";

import type { UploadFile } from "antd/es/upload/interface";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Props สำหรับ ProductSKUCreate Component
 * ใช้สำหรับสร้าง/แก้ไข/คัดลอก SKU
 */
interface ProductVariationCreateProps {
  setVariants: (variants: VariantsInput) => void; // ฟังก์ชันสำหรับอัปเดต variants array
  variants: VariantsInput; // ข้อมูล variants ที่มีอยู่
  setPrices: (prices: PricesInput) => void; // ฟังก์ชันสำหรับอัปเดต prices array
  prices: PricesInput; // ข้อมูล prices ที่มีอยู่
  onClose: () => void; // ฟังก์ชันสำหรับปิด Modal
  copyMode?: boolean; // โหมดคัดลอก (optional)
  editMode?: boolean; // โหมดแก้ไข (optional)
  copyData?: { variant: Variant, price: Price } | null; // ข้อมูลสำหรับคัดลอก (optional)
  editData?: { variant: Variant, price: Price, index: number } | null; // ข้อมูลสำหรับแก้ไข (optional)
  skuImage: UploadFile[] | undefined;
  setSkuImage: (skuImage: UploadFile[] | undefined) => void;
  originalPrices?: PricesInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้สำหรับ restore c1-c3 (optional)
  originalVariants?: VariantsInput; // ✅ เก็บค่าจากฐานข้อมูลเพื่อใช้ restore shipping_method + dimensions (optional)
  factoryId?: string; // ✅ รับ factoryId
  isEditMode?: boolean; // ✅ โหมดแก้ไขสินค้า (optional)
  productId?: string; // ✅ รับ productId
  percentCommission?: number; // ✅ percent_commission จากประเภทสินค้าที่เลือก
  categoryName?: string; // ✅ ชื่อประเภทสินค้าที่เลือก
  onStockToggled?: () => void;
  /** true = ซ่อนแท็บราคา แสดงเฉพาะแท็บคลัง (สำหรับเปิดผ่าน "แก้ไข" จาก inventory grid) */
  warehouseOnly?: boolean;
  /** แท็บคลัง: สร้าง SKU ใหม่แล้วไม่ปิด modal → ให้ host สลับ modal ไปแก้ SKU ที่เพิ่งสร้าง */
  onSkuCreated?: (variant: Variant, price: Price, index: number) => void;
}



/**
 * ProductSKUCreate Component
 * ใช้สำหรับสร้าง/แก้ไข/คัดลอก SKU ใน Modal
 */
const ProductVariationCreate: FC<ProductVariationCreateProps> = ({
  setVariants,
  setPrices,
  onClose,
  variants,
  prices,
  copyMode = false, // โหมดคัดลอก (default: false)
  editMode = false, // โหมดแก้ไข (default: false)
  copyData = null, // ข้อมูลสำหรับคัดลอก (default: null)
  editData = null, // ข้อมูลสำหรับแก้ไข (default: null)
  skuImage,
  setSkuImage,
  originalPrices = [],
  originalVariants = [],
  factoryId,
  isEditMode = false, // ✅ โหมดแก้ไขสินค้า (default: false)
  productId, // ✅ รับ productId
  percentCommission = 0, // ✅ percent_commission จากประเภทสินค้าที่เลือก
  categoryName = "", // ✅ ชื่อประเภทสินค้าที่เลือก
  onStockToggled,
  warehouseOnly = false,
  onSkuCreated,
}) => {
  // Form instance สำหรับจัดการข้อมูล
  const [form] = Form.useForm();
  // ✅ guard: กัน setTimeout packaging_type_id re-inject หลัง user interact
  const hasPopulatedRef = useRef(false);
  const [speedHasSearched, setSpeedHasSearched] = useState(false); // ✅ ซ่อนการแสดงผลฝั่งขวาก่อนค้นหา (กันสับสน)
  const [shouldBlinkButton, setShouldBlinkButton] = useState(false); // ✅ สำหรับควบคุมการกระพริบของปุ่มค้นหาบริษัทขนส่ง

  // ✅ ดึง sell_through_line, payment_gateway_fee จาก Redux state
  const userProfile = useAppSelector((state) => state.auth.profile);
  const sellThroughLine = useAppSelector((state) => state.auth.profile?.sell_through_line ?? false);
  const paymentGatewayFee = useAppSelector((state) => state.auth.profile?.payment_gateway_fee ?? null);

  // ✅ ดึง factoryData สำหรับ merchants (แทน userProfile)
  const targetFactoryId = factoryId || (userProfile?.user?.role?.ref_type === "factory" ? userProfile?.user?.role?.ref_id : "");
  const { data: factoryData } = useGetFactoryById(targetFactoryId || "");

  // ✅ useCallback เพื่อ memoize (rerender-functional-setstate)
  const getDefaultMerchantCodes = useCallback((): string[] => {
    const defaultMerchant = factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default);
    return defaultMerchant ? [defaultMerchant.merchant_id] : [];
  }, [factoryData]);

  // ✅ Hook สำหรับเรียก API Update SKU
  const updateSkuMutation = useUpdateProductSku();

  // State สำหรับจัดการข้อมูล SKU (Single-Step)
  const [currentSkuData, setCurrentSkuData] = useState<{
    variant: VariationData | null;
    price: VariationPrice | null;
  }>({ variant: null, price: null }); // ข้อมูล SKU ปัจจุบันที่กำลังสร้าง

  // State สำหรับจัดการ form validation
  const [formValidate, setFormValidate] = useState<boolean>(true); // สถานะการตรวจสอบ form

  // State สำหรับจัดการ loading และ submission
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // สถานะการบันทึก

  // State สำหรับจัดการ Draft Mode
  const [isDraftSave, setIsDraftSave] = useState<boolean>(false); // บันทึกร่างหรือเผยแพร่

  const [modalTab, setModalTab] = useState<"price" | "warehouse">(
    warehouseOnly ? "warehouse" : "price"
  );
  const [linkedDraftRows, setLinkedDraftRows] = useState<SkuLinkedStockDraftRow[]>(
    []
  );
  /** สถานะจำกัดสต็อกจากแท็บคลัง (sync กับ toggle ใน modal) */
  const [warehouseIsStock, setWarehouseIsStock] = useState(false);
  /** true = แท็บคลังโหลด/ไฮเดรตข้อมูลเสร็จแล้ว → ปุ่มบันทึกทุกปุ่มส่ง linked_stock ได้ปลอดภัย (กัน wipe ตอนยังไม่โหลด) */
  const [linkedLoaded, setLinkedLoaded] = useState(false);
  /** กันแท็บคลังโหลด list ทับหลัง parent reload จาก sync */
  const [linkedReloadToken, setLinkedReloadToken] = useState(0);
  const [linkedUiMode, setLinkedUiMode] = useState<LinkedStockUiMode>("basic");
  /** ปุ่มที่กดในแท็บคลัง: true = "บันทึกและปิด" (ส่งผ่าน form.submit() ไม่ได้ เลยเก็บใน ref) */
  const closeAfterRef = useRef(false);
  /** หน้าแก้ไข: id ของ SKU ก่อนสร้าง SKU ใหม่ — response ไม่มี id จึงรอ refetch แล้วหาตัวที่เพิ่มขึ้น */
  const [pendingSkuIds, setPendingSkuIds] = useState<Set<string> | null>(null);
  const stockEditCtx = useSkuModalStockEditOptional();

  /** ปิด side-panels ทั้งหมดก่อน switch กลับ price tab */
  const switchToPriceTab = useCallback(() => {
    stockEditCtx?.closeStockEdit();
    stockEditCtx?.closeIngredientPicker();
    stockEditCtx?.setModalLayoutTab('price');
    setModalTab('price');
  }, [stockEditCtx]);

  useEffect(() => {
    if (!stockEditCtx) return;
    stockEditCtx.registerOnStockSaved(() => {
      setLinkedReloadToken((t) => t + 1);
      onStockToggled?.();
    });
  }, [stockEditCtx, onStockToggled]);

  /** expand ซ้าย/ขวา ใช้ได้เฉพาะ tab คลัง — สลับไปราคา = ปิดทันที */
  const closeStockEditFn = stockEditCtx?.closeStockEdit;
  const closeIngredientPickerFn = stockEditCtx?.closeIngredientPicker;
  useEffect(() => {
    if (modalTab !== "warehouse") {
      closeStockEditFn?.();
      closeIngredientPickerFn?.();
    }
  }, [modalTab, closeStockEditFn, closeIngredientPickerFn]);

  /** ปิด modal (component unmount จาก destroyOnClose) → ปิด expand ทั้งหมด */
  useEffect(() => {
    return () => {
      closeStockEditFn?.();
      closeIngredientPickerFn?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skuItemIdForLinked =
    editData?.variant?.items?.[0]?.id ?? currentSkuData.variant?.items?.[0]?.id;

  const linkedSkuItem = useMemo(() => {
    const id = skuItemIdForLinked;
    if (!id) return editData?.variant?.items?.[0] ?? null;
    for (const v of variants) {
      const item = v.items?.[0];
      if (item?.id === id) return item;
    }
    // หน้าเพิ่มสินค้า + คัดลอก SKU: ตัวที่คัดลอกยังไม่อยู่ใน variants → ใช้ค่าคลังที่คัดลอกมา
    if (!isEditMode && copyMode) return (currentSkuData.variant?.items?.[0] as VariantItem | undefined) ?? null;
    return editData?.variant?.items?.[0] ?? null;
  }, [variants, skuItemIdForLinked, editData, isEditMode, copyMode, currentSkuData.variant]);

  /** SKU นี้มีใน DB แล้ว (หน้าแก้ไข + แก้ SKU เดิม) — ไม่ใช่ = SKU ใหม่/คัดลอก/SKU ในหน้าเพิ่มสินค้า */
  const isSkuInDb = isEditMode && editMode && !!editData;

  // ชื่อ/รูปของ SKU ในแท็บ "คลัง" = ค่าปัจจุบันในฟอร์มแท็บ "ราคา" เสมอ (แม้ยังไม่ได้กดบันทึก)
  // ห้ามใช้ prop skuImage — เป็น state ระดับการ์ดที่ใช้ร่วมทุก SKU และไม่ถูก reset ตอนเปิด SKU อื่น
  // (เปิด SKU ใหม่แล้วจะเห็นรูปของ SKU ที่อัปโหลดล่าสุด)
  // undefined = ฟอร์มยังไม่ถูกเติมค่า → ใช้ค่าที่บันทึกไว้ของ SKU นั้นแทน
  const watchedSkuNameTop = Form.useWatch(["sku_items", 0, "name"], form);
  const watchedSkuNameNested = Form.useWatch(["sku_items", 0, "items", 0, "name"], form);
  const watchedSkuName = watchedSkuNameTop ?? watchedSkuNameNested;
  const skuNameForWarehouse =
    watchedSkuName !== undefined && watchedSkuName !== null
      ? String(watchedSkuName).trim()
      : linkedSkuItem?.name || "";

  const watchedSkuImageTop = Form.useWatch(["sku_items", 0, "image_id"], form);
  const watchedSkuImageNested = Form.useWatch(["sku_items", 0, "items", 0, "image_id"], form);
  const watchedSkuImageList: any[] | undefined = Array.isArray(watchedSkuImageTop)
    ? watchedSkuImageTop
    : Array.isArray(watchedSkuImageNested)
      ? watchedSkuImageNested
      : undefined;
  const savedSkuImageUrl: string | null | undefined = linkedSkuItem
    ? (linkedSkuItem as any).image_url ||
      (linkedSkuItem.image_id ? `${backendUrl}images/${linkedSkuItem.image_id}` : null)
    : undefined;
  const firstWatchedImage = watchedSkuImageList?.[0];
  const skuImageUrlForWarehouse: string | null | undefined = watchedSkuImageList
    ? firstWatchedImage?.url || firstWatchedImage?.response?.url || firstWatchedImage?.thumbUrl || null
    : savedSkuImageUrl;

  useEffect(() => {
    setWarehouseIsStock(linkedSkuItem?.is_stock ?? false);
  }, [linkedSkuItem?.is_stock, skuItemIdForLinked]);

  // หมายเหตุ: การล้าง linkedDraftRows ตอนเปลี่ยน SKU อยู่ในแท็บคลัง (reset effect ของ SkuModalLinkedStockTab)
  // ห้ามล้างจากตรงนี้ — effect ของ parent รันหลัง child ใน commit เดียวกัน (ตอน mount ของ destroyOnClose)
  // จะลบค่าที่แท็บคลังเพิ่งไฮเดรต (ค่าที่ตั้งไว้แล้วหายตอนกด "แก้ไข" เปิดกลับมา)

  /** หน้าเพิ่มสินค้า: ค่าคลังที่ตั้งไว้แล้วของ SKU นี้ (เก็บใน variants ของหน้า รอส่งไปกับ POST /product) */
  const storedLocalDraft: SkuLocalStockDraft | null = isEditMode
    ? null
    : (linkedSkuItem as any)?.linked_stock_draft ?? null;
  const [initialStockQty, setInitialStockQty] = useState(0);

  /** หน้าเพิ่มสินค้า: SKU อื่นในสินค้านี้ที่ตั้ง "จัดการสต็อกแยก" ไว้ → เลือกผูกในโหมดขั้นสูงได้ (backend แปลงเป็น id จริงตอนสร้าง) */
  const localStockOptions = useMemo<ProductStockPickerRow[] | undefined>(() => {
    if (isEditMode) return undefined;
    return variants
      .map((v) => v.items?.[0] as any)
      .filter((it) => it?.id && it.id !== skuItemIdForLinked && it.is_stock && it.linked_stock?.is_initial_stock_sku)
      .map((it) => {
        const qty = Number(it.linked_stock?.initial_quantity) || 0;
        return {
          id: `${LOCAL_STOCK_REF_PREFIX}${it.id}`,
          type: "sku",
          ref_label: it.name || "",
          sku_id: it.id,
          image_url: it.image_url || null,
          quantity: qty,
          holding_quantity: 0,
          stock_balance: qty,
          is_active: true,
        };
      });
  }, [isEditMode, variants, skuItemIdForLinked]);

  // แถวที่ผูกกับ SKU ในสินค้านี้: ใช้ชื่อ/ยอดล่าสุดของ SKU นั้น (อาจถูกแก้หลังผูก)
  const localStockDraft = useMemo<SkuLocalStockDraft | null>(() => {
    if (!storedLocalDraft) return null;
    return {
      ...storedLocalDraft,
      rows: storedLocalDraft.rows.map((r) => {
        if (!r.ref_id.startsWith(LOCAL_STOCK_REF_PREFIX)) return r;
        const cur = localStockOptions?.find((o) => o.id === r.ref_id);
        return cur
          ? { ...r, ref_label: cur.ref_label, ref_balance: cur.stock_balance }
          : { ...r, ref_label: `${r.ref_label} (ถูกลบหรือไม่ได้จัดการสต๊อกหลักแล้ว)`, ref_balance: 0 };
      }),
    };
  }, [storedLocalDraft, localStockOptions]);
  useEffect(() => {
    setInitialStockQty(Number((linkedSkuItem as any)?.linked_stock?.initial_quantity) || 0);
  }, [linkedSkuItem, skuItemIdForLinked]);

  const handleModalStockToggle = useCallback(
    async (checked: boolean) => {
      const id = skuItemIdForLinked;
      if (!id) return;
      // อัปเดตเฉพาะสถานะภายใน modal (pending) — ยังไม่ดันค่าไปที่ variants ของหน้าแก้สินค้า
      // ค่าจะมีผลจริงต่อเมื่อกด "บันทึก" เท่านั้น (กัน toggle เล่นแล้วปิดทิ้งโดยไม่บันทึก)
      setWarehouseIsStock(checked);
    },
    [skuItemIdForLinked]
  );

  //refesh product by id   queryKey: ['product', productId],
  const queryClient = useQueryClient();

  /**
   * ฟังก์ชันสำหรับจัดการ Copy Mode
   * นำข้อมูลจาก copyData มาใช้ในการสร้าง SKU ใหม่ (ยกเว้น รูปภาพ, รหัส, ชื่อ)
   */
  const handleCopyMode = useCallback(() => {
    if (copyData) {
      const copiedVariant = {
        ...copyData.variant,
        id: uuidv4(), // สร้าง ID ใหม่
        name: "", // ล้างชื่อ ให้ผู้ใช้กรอกใหม่
        items: copyData.variant.items.map(item => ({
          ...item,
          ...(!isEditMode && (item as any).linked_stock
            ? {
                // คัดลอกโหมด/ไอเท็มที่ผูก แต่ไม่คัดลอกจำนวนเริ่มต้น (ของจริงคนละก้อน)
                linked_stock: { ...(item as any).linked_stock, initial_quantity: 0 },
                stock: undefined,
              }
            : {}),
          id: uuidv4(), // สร้าง ID ใหม่
          name: "", // ล้างชื่อ ให้ผู้ใช้กรอกใหม่
          image_id: "", // ล้างรูปภาพ (ตาม requirement)
          url: "", // ล้าง URL (ตาม requirement)
          product_code: "", // ล้างรหัสสินค้า (ตาม requirement)
          // คัดลอกข้อมูลอื่นๆ ครบถ้วน
          product_capacity: item.product_capacity,
          product_quantity: item.product_quantity,
          lead_time: item.lead_time,
          preparation_time: item.preparation_time || 1, // ✅ คัดลอก preparation_time
          width: item.width,
          length: item.length,
          height: item.height,
          weight: item.weight,
          packaging_type_id: item.packaging_type_id || null, // ✅ เพิ่ม packaging_type_id
          shipping_method: item.shipping_method || "self", // ✅ เปลี่ยน default เป็น "ส่งเอง"
          is_active: true, // ✅ ตั้งค่าเป็น true เสมอ (CopyMode)
          merchants: toMerchantIdArray((item as any).merchants),
          merchant_id: (item as any).merchant_id || "",
        }))
      };

      const copiedPrice = {
        ...copyData.price,
        variant_1_item_id: copiedVariant.items[0]?.id || "",
        variant_2_item_id: "",
        // คัดลอกราคาทั้งหมด (ยกเว้น variant_1_item_id ที่เปลี่ยนแล้ว)
        factory_price: copyData.price.factory_price,
        wholesale_price: copyData.price.wholesale_price,
        retail_price: copyData.price.retail_price,
        // ✅ เปิด copy ครั้งแรก: init input_market_price = retail_price จาก DB (เดิม init เป็น 0 ให้ผู้ใช้กรอกใหม่ — เปลี่ยนตาม requirement ใหม่)
        input_market_price: copyData.price.retail_price || 0,
        vip_price: copyData.price.vip_price,
        base_factory_price: copyData.price.base_factory_price,
        base_wholesale_price: copyData.price.base_wholesale_price,
        base_retail_price: copyData.price.base_retail_price,
        base_vip_price: copyData.price.base_vip_price,
        transport_price: copyData.price.transport_price,
        api_transport_carrier_code: copyData.price.api_transport_carrier_code || undefined,
        api_transport_logo: copyData.price.api_transport_logo || undefined,
        api_transport_price: copyData.price.api_transport_price || 0,
        add_on_api_transport_price: copyData.price.add_on_api_transport_price || 0,
        self_transport_price: copyData.price.self_transport_price ?? 0,
        percent_payment_gateway: copyData.price.percent_payment_gateway,
        payment_getway: copyData.price.payment_getway || 0, // ✅ เพิ่ม: Payment Gateway
        commistion_lv1: copyData.price.commistion_lv1 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 1
        commistion_lv2: copyData.price.commistion_lv2 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 2
        commistion_lv3: copyData.price.commistion_lv3 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 3
        commistion_lv4: copyData.price.commistion_lv4 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 4
        commistion_lv5: copyData.price.commistion_lv5 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 5
        total_commission: copyData.price.total_commission || 0, // ✅ เพิ่ม: ค่าคอมมิชชั่นและค่าดำเนินการ
        unit_factory_price: copyData.price.unit_factory_price || "0", // ✅ เพิ่ม: ราคาตัวแทนต่อหน่วย
        unit_wholesale_price: copyData.price.unit_wholesale_price || "0", // ✅ เพิ่ม: ราคาขายส่งต่อหน่วย
        unit_retail_price: copyData.price.unit_retail_price || "0", // ✅ เพิ่ม: ราคาตลาดต่อหน่วย
        unit_vip_price: copyData.price.unit_vip_price || "0", // ✅ เพิ่ม: ราคา VIP ต่อหน่วย
        product_unit: copyData.price.product_unit ?? undefined, // ✅ เพิ่ม: หน่วยสินค้า
      };

      setCurrentSkuData({ variant: copiedVariant, price: copiedPrice });
    }
  }, [copyData, isEditMode]);

  /**
   * ฟังก์ชันสำหรับจัดการ Edit Mode
   * นำข้อมูลจาก editData มาใช้ในการแก้ไข SKU ที่มีอยู่
   */
  const handleEditMode = useCallback(() => {
    if (editData) {
      setCurrentSkuData({
        variant: editData.variant,
        price: editData.price
      });
    }
  }, [editData]);

  /**
   * ฟังก์ชันสำหรับสร้าง SKU ใหม่ (โหมดปกติ)
   */
  const handleNewMode = useCallback(() => {
    // ✅ [requirement 2026-10-16] ไม่มี merchant เลย (ทั้ง speed และ thaipost) → default = "ส่งเอง"
    //    มี merchant อย่างน้อย 1 ชนิด → default = "ส่งกับพาร์ทเนอร์แบ่งเบา" (พฤติกรรมเดิม)
    const hasAnyMerchant =
      (factoryData?.factory?.merchants?.speed?.length ?? 0) > 0 ||
      (factoryData?.factory?.merchants?.thaipost?.length ?? 0) > 0;
    const defaultShippingMethod = hasAnyMerchant ? "bangbow" : "self";

    const newVariant: VariationData = {
      id: uuidv4(),
      name: "",
      capacity: 0,
      items: [{
        id: uuidv4(),
        name: "",
        image_id: "",
        product_code: "",
        product_capacity: 0,
        product_quantity: 0,
        lead_time: 0,
        preparation_time: 1, // ✅ ค่า default 1 วัน
        width: 0,
        length: 0,
        height: 0,
        weight: 0,
        packaging_type_id: null,
        shipping_method: defaultShippingMethod, // ✅ default: มี merchant = bangbow, ไม่มี merchant เลย = self
        is_active: true,
      }]
    };

    const newPrice: VariationPrice = {
      variant_1_item_id: newVariant.items[0].id,
      variant_2_item_id: "",
      factory_price: 0,
      wholesale_price: 0,
      retail_price: 0,
      input_market_price: 0, // ✅ ราคาตลาดด้านบน (ใช้คำนวณ)
      vip_price: 0,
      base_factory_price: 0,
      base_wholesale_price: 0,
      base_retail_price: 0,
      base_vip_price: 0,
      transport_price: 0,
      api_transport_carrier_code: undefined,
      api_transport_logo: undefined,
      api_transport_price: 0,
      add_on_api_transport_price: 0,
      // ✅ [requirement 2026-10-16] ไม่ตั้งค่าเริ่มต้นเป็น 0 — ปล่อยว่างเพื่อบังคับให้กรอกเองตอนเลือก "ส่งเอง"
      self_transport_price: undefined,
      percent_payment_gateway: 0,
      total_commission: 0, // ✅ เพิ่ม: ค่าคอมมิชชั่นและค่าดำเนินการ
    };

    setCurrentSkuData({ variant: newVariant, price: newPrice });
  }, [factoryData]);

  /**
   * ✅ Refactored: ดึง logic สร้าง API payload และเรียก API ออกมาเป็นฟังก์ชันเดียว
   * ใช้ร่วมกันระหว่าง editMode + !editMode (เดิมเป็น 2 ก้อนซ้ำกัน ~200 บรรทัด)
   */
  const isSelfSkuRow = (r: SkuLinkedStockDraftRow) =>
    r.source_kind === "sku" && r.ref_stock_sku_id === skuItemIdForLinked;

  /** linked_stock ที่ส่ง backend — ใช้ทั้ง sku/update (หน้าแก้ไข) และ POST /product (หน้าเพิ่มสินค้า) */
  const buildLinkedStockPayload = (mode: LinkedStockUiMode, rows: SkuLinkedStockDraftRow[]) => {
    const isBasicMode = mode === "basic";
    const nonSelfRows = rows.filter((r) => !isSelfSkuRow(r));
    return {
      is_stock: warehouseIsStock,
      // basic = ใช้สต็อก SKU หลักเสมอ; advanced = ดูว่ามี self sku ในตารางหรือไม่
      // เมื่อ ENABLE_ADVANCED_SELF_STOCK ปิด → โหมดขั้นสูงไม่ส่ง is_initial (เหลือเฉพาะ basic)
      is_initial_stock_sku:
        warehouseIsStock &&
        (isBasicMode || (ENABLE_ADVANCED_SELF_STOCK && rows.some(isSelfSkuRow))),
      // basic = ไม่ผูก linked items (อิงเฉพาะของพื้นฐาน); advanced = ผูกตามตารางไอเท็ม
      items: warehouseIsStock && !isBasicMode
        ? nonSelfRows.map((row, index) => ({
            // SKU ในสินค้าเดียวกันที่ยังไม่มีใน DB → ส่ง id ชั่วคราวให้ backend แปลงเอง
            ...(row.ref_id.startsWith(LOCAL_STOCK_REF_PREFIX)
              ? { ref_id: "", ref_client_key: row.ref_id.slice(LOCAL_STOCK_REF_PREFIX.length) }
              : { ref_id: row.ref_id }),
            ref_type: (row.ref_type === "product_recipe" ? "recipe" : "stock") as any,
            sequence: index,
            is_active: true,
            quantity: normalizeLinkedQuantity(row.quantity),
          }))
        : [],
    };
  };

  /**
   * หน้าเพิ่มสินค้า: ค่าคลังที่เก็บลง SKU ใน state ของหน้า
   * - linked_stock → ส่งไปกับ POST /product (backend ตั้งคลัง + สต็อกเริ่มต้นใน transaction เดียวกับการสร้าง)
   * - linked_stock_draft → ใช้ไฮเดรตแท็บคลังเมื่อเปิด SKU นี้อีกครั้ง
   * - stock → ให้การ์ด SKU แสดงยอดได้
   */
  const buildLocalStockFields = () => {
    if (!warehouseIsStock) {
      return { is_stock: false, linked_stock: undefined, linked_stock_draft: undefined, stock: undefined };
    }
    // ไม่ได้เปิดแท็บคลังรอบนี้ → ใช้ค่าที่ตั้งไว้เดิม
    const draft: SkuLocalStockDraft = linkedLoaded
      ? { mode: linkedUiMode, rows: linkedDraftRows.filter((r) => !isSelfSkuRow(r)) }
      : localStockDraft ?? { mode: "basic", rows: [] };
    const qty = draft.mode === "basic" ? initialStockQty : 0;
    // ยอดที่การ์ดแสดง: พื้นฐาน = สต็อกเริ่มต้น / ขั้นสูง = ขายได้เท่าที่ไอเท็มที่ผูกไว้พอ (เหมือนในแท็บคลัง)
    const sellable =
      draft.mode === "basic"
        ? qty
        : draft.rows.length
          ? Math.min(
              ...draft.rows.map((r) =>
                Math.floor((Number(r.ref_balance) || 0) / normalizeLinkedQuantity(r.quantity))
              )
            )
          : 0;
    return {
      is_stock: true,
      linked_stock: { ...buildLinkedStockPayload(draft.mode, draft.rows), initial_quantity: qty },
      linked_stock_draft: draft,
      stock: { stock_quantity: sellable, holding_quantity: 0, stock_balance: sellable },
    };
  };

  const callUpdateProductSkuApi = async (
    skuItemId: any[],
    newPrice: VariationPrice,
    options?: { includeLinkedStock?: boolean }
  ) => {
    const singleVariant = skuItemId[0];
    const singleItem = singleVariant?.items?.[0];
    if (!singleVariant || !singleItem) {
      message.error('ไม่พบข้อมูล SKU ที่จะส่ง');
      return;
    }

    // ✅ Fix: หา variant.id "จริงจาก DB" เพื่อป้องกันปัญหา 404 "variant_sku not found"
    //   - editMode=true → ใช้ editData.variant.id (แหล่ง DB ที่ชัดเจน)
    //   - editMode=false แต่ singleItem.id เป็น DB id (timing/edge case) → resolve จาก originalVariants/variants
    //   - resolve ไม่ได้ → ถือเป็น "SKU ใหม่" ไม่ส่ง variants[0].id
    // เหตุผล: ก่อนหน้านี้ payload อาจส่ง items[0].id (id เดิมใน DB) แต่ไม่ส่ง variants[0].id
    // ทำให้ backend หา variant ด้วย variant_name + product_id แล้วเจอ variant ผิดตัว → SKU lookup โดน
    // filter variant_id ไม่ตรง → 404
    const rawItemId = editMode && editData
      ? editData.variant.items[0]?.id
      : singleItem.id;

    // หา variant.id จริงจาก DB ผ่าน originalVariants/variants prop (match ด้วย item.id)
    // ใช้ logic เดียวกับ SKUModalForm.tsx (~บรรทัด 886-888) เพื่อความสม่ำเสมอ
    const matchedFromOriginal =
      originalVariants.find((v) => v.items?.[0]?.id === rawItemId) ??
      variants.find((v) => v.items?.[0]?.id === rawItemId);

    const resolvedVariantId: string | undefined =
      (editMode && editData?.variant?.id) ||
      matchedFromOriginal?.id ||
      undefined; // ← ไม่มี DB id = ถือเป็น SKU ใหม่ (ไม่ส่ง id ลง backend)

    // ✅ ปรับ itemId: ถ้า resolve variant.id ไม่ได้ = เป็น SKU ใหม่
    //   - "ห้าม" ส่ง uuid ลอยๆ เพราะ backend (product-sku.service.ts บรรทัด 100-107)
    //     ตรวจ if (psv?.id) แล้วไปหาใน DB ทันที ถ้าไม่เจอ → โยน 404 "variant_sku not found"
    //   - ต้องส่ง "" (falsy) เพื่อให้ backend เข้า else branch แล้วสร้าง variant_sku ใหม่
    //     พร้อมทั้ง backend จะ fallback variant_1_item_id ให้เอง (`|| variant_sku.id`)
    const itemId = resolvedVariantId
      ? rawItemId               // มี variant.id จริง → ใช้ item.id เดิม (edit)
      : "";                      // ไม่มี variant.id → ส่ง "" ให้ backend สร้างใหม่ (create)

    // เก็บ variantId ไว้ตามเดิมเผื่อ debug/log (ไม่ใช้สร้าง payload โดยตรงแล้ว)
    const variantId = resolvedVariantId || currentSkuData.variant?.id || uuidv4();

    const formattedPrice = buildFormattedPrice({
      editMode,
      editData,
      itemId,
      newPrice,
    });

    const singleItemDto = buildSingleItemDto({
      editMode,
      editData,
      singleItem: { ...singleItem, id: itemId },
      formattedPrice,
      getDefaultMerchantCodes,
    });

    const convertedVariants: ProductVariantDto[] = [{
      // ✅ ส่ง variants[0].id เมื่อ resolve เป็น DB id ได้เท่านั้น (ไม่ผูกกับ editMode อย่างเดียว)
      ...(resolvedVariantId ? { id: resolvedVariantId } : {}),
      name: editMode && editData ? "ตัวเลือก" : "ตัวเลือก",
      capacity: currentSkuData.variant?.capacity ?? singleVariant.capacity ?? 100,
      items: [singleItemDto]
    }];

    // หมายเหตุ: variantId เก็บไว้สำหรับ backward-compat / debug — ไม่ได้ใช้ใน payload โดยตรง
    void variantId;

    const apiPayload: UpdateProductSkuDto = {
      product_id: productId!,
      variants: convertedVariants,
      prices: [],
    };

    if (options?.includeLinkedStock) {
      apiPayload.linked_stock = {
        product_id: productId!,
        ...buildLinkedStockPayload(linkedUiMode, linkedDraftRows),
        // SKU ใหม่ในหน้าแก้ไข: สต็อกเริ่มต้นบันทึกพร้อมการสร้าง SKU (backend ทำ inbound ใน transaction เดียวกัน)
        ...(!isSkuInDb && linkedUiMode === "basic" && initialStockQty > 0
          ? { initial_quantity: initialStockQty }
          : {}),
      };
    }

    try {
      // backend คืน HTTP 200 เสมอ แม้ทำไม่สำเร็จ (HttpExceptionFilter)
      // → axios ไม่ throw ต้องเช็ค status ในตัว response เอง
      const result: any = await updateSkuMutation.mutateAsync(apiPayload);
      if (result?.status === 'failed') {
        throw result;
      }
      message.success(editMode ? 'อัปเดต SKU สำเร็จ' : 'เพิ่ม SKU สำเร็จ');
      queryClient.refetchQueries({
        queryKey: ['product', productId],
        type: 'all',
      });
    } catch (error: any) {
      // กรณีพิเศษ: เปลี่ยนพื้นฐาน→ขั้นสูงไม่ได้ เพราะมีสินค้าอื่นผูกสต็อกนี้ไว้
      if (handleLinkedStockDependentsError(error)) {
        // marker บอก caller ว่าแสดง modal แล้ว ไม่ต้องเด้ง error ซ้ำ
        const handled: any = new Error('LINKED_STOCK_DEPENDENTS_HANDLED');
        handled.__linkedStockHandled = true;
        throw handled;
      }
      // error อื่น ๆ ปล่อยให้ caller (onFinish) เป็นผู้แสดงข้อความ
      throw error;
    }
  };


  /**
   * ฟังก์ชันหลักสำหรับบันทึก SKU
   * รองรับทั้งโหมดสร้างใหม่, แก้ไข, และคัดลอก
   */
  // skipPriceChecks = true เฉพาะตอนกดปุ่ม "บันทึกคลัง" เท่านั้น
  // (AntD เรียก onFinish(values) อาร์กิวเมนต์เดียว → ทุกทางเดิมยังตรวจราคาเหมือนเดิม)
  const onFinish = async (values: any, skipPriceChecks = false) => {
    if (!skipPriceChecks) {
      // ตรวจสอบ validation ก่อนบันทึก
      if (!formValidate) {
        return;
      }
      // ตรวจสอบข้อมูลใน sku_items[0] — ตรวจสอบราคา
      const firstSkuItem = values.sku_items[0];
      if (firstSkuItem && !validatePriceOrder(firstSkuItem)) {
        setFormValidate(false);
        return;
      }
    }
    /*
        // ✅ ตรวจสอบว่า total_commission ต้องไม่มากกว่า base_factory_price (Confirm Modal)
        // ใช้ logic เดียวกับการดึงค่าเพื่อสร้าง payload
        const checkItem = values.sku_items[0]?.items?.[0] ?? values.sku_items[0];
        const checkBaseFactory = parsePrice(checkItem?.base_factory_price);
        const checkTotalComm = parsePrice(checkItem?.total_commission);
        if (checkTotalComm > 0 && checkTotalComm > checkBaseFactory) {
          Modal.confirm({
            title: "ราคาตั้งต้นต่ำกว่าค่าคอมมิชชั่นเดิม",
            content: `ราคาตั้งต้น (${checkBaseFactory.toLocaleString()}) ต่ำกว่าค่าคอมมิชชั่น (${checkTotalComm.toLocaleString()}) ต้องการรีเซ็ตค่าคอมมิชชั่นเป็น 0 หรือไม่?`,
            okText: "รีเซ็ตค่าคอมมิชชั่น",
            cancelText: "ยกเลิก",
            okType: "danger",
            onOk: () => {
              // รีเซ็ตค่าคอมมิชชั่นทั้งหมดเป็น 0
              form.setFieldValue(["sku_items", 0, "total_commission"], 0);
              form.setFieldValue(["sku_items", 0, "commistion_lv1"], 0);
              form.setFieldValue(["sku_items", 0, "commistion_lv2"], 0);
              form.setFieldValue(["sku_items", 0, "commistion_lv3"], 0);
              form.setFieldValue(["sku_items", 0, "commistion_lv4"], 0);
              form.setFieldValue(["sku_items", 0, "commistion_lv5"], 0);
              
              // โฟกัสไปที่ช่อง total_commission
              // หมายเหตุ: ใช้ setTimeout เพื่อให้แน่ใจว่าค่าถูก set เรียบร้อยแล้วก่อน focus
              setTimeout(() => {
                 form.scrollToField(["sku_items", 0, "total_commission"], { block: 'center' });
                 form.getFieldInstance(["sku_items", 0, "total_commission"])?.focus?.();
              }, 100);
            },
          });
          return;
        }
        setIsSubmitting(true);
    */
    try {
      if (!isEmpty(values.sku_items)) {
        const skuItemId = values.sku_items.map((item: any) => {
          // ✅ บาง flow/เวอร์ชันของฟอร์มอาจเก็บค่าบางส่วนไว้ที่ item.items[0]
          // เพื่อความ robust ให้ fallback ไปที่ชั้นนั้น (ถ้าไม่มีให้ใช้ item เดิม)
          const option = item?.items?.[0] ?? item;
          return {
            capacity: item.capacity || 0,
            name: item.name || "",
            items: [{
              name: item.name || "",
              image_id: item.image_id && item.image_id.length > 0 ? item.image_id[0]?.id || "" : "",
              image_url: item.image_id && item.image_id.length > 0 ? item.image_id[0]?.url || "" : "",
              id: editData?.variant.items[0].id || uuidv4(),
              product_capacity: parseInt(option.product_capacity) || 0,
              product_quantity: parseInt(option.product_quantity) || 0,
              lead_time: parseInt(option.lead_time) || 0,
              preparation_time: parseInt(option.preparation_time) || 1, // ✅ ดึง preparation_time
              width: parseFloat(option.width) || 0,
              length: parseFloat(option.length) || 0,
              height: parseFloat(option.height) || 0,
              weight: parseInt(option.weight) || 0,
              packaging_type_id: (item.shipping_method || option.shipping_method || "self") === "self" ? null : (option.packaging_type_id || item.packaging_type_id || null),
              // ✅ Persist วิธีการจัดส่งเพื่อไม่ให้ reopen แล้ว fallback เป็น "self"
              shipping_method: item.shipping_method || option.shipping_method || "self",
              // ✅ ส่งเอง → บังคับปิด COD (COD ใช้ได้เฉพาะ Partner แบ่งเบา / ไปรษณีย์ไทย)
              is_cod:
                (item.shipping_method || option.shipping_method || "self") === "self"
                  ? false
                  : (option.is_cod ?? item.is_cod ?? false),
              // บันทึกร่าง → ปิดการขายชั่วคราว; เผยแพร่ → ใช้ค่าจากฟอร์ม (เปิด/ปิดการขาย)
              is_active: isDraftSave ? false : (typeof item.is_active === "boolean" ? item.is_active : true),
              is_draft: isDraftSave, // ส่งสถานะร่างไปยัง backend
              product_code: option.product_code || item.product_code || "",
              partner_transport_id: (item.shipping_method || option.shipping_method || "self") === "self" ? "" : (item.partner_id || ""),
              partner_transport_brand_id: (item.shipping_method || option.shipping_method || "self") === "self" ? "" : (item.brand_id || ""),
              // ✅ ส่งเอง → ไม่ส่ง merchants ของ Speed; bangbow → ใช้ค่าฟอร์มหรือ default
              merchants:
                (item.shipping_method || option.shipping_method || "self") === "self"
                  ? []
                  : item.merchants && item.merchants.length > 0
                    ? toMerchantIdArray(item.merchants)
                    : getDefaultMerchantCodes(),
              // merchant_id:
              //   item.merchant_id ||
              //   factoryData?.factory?.merchants?.find((m: { is_default: boolean }) => m.is_default)?.merchant_id ||
              //   "",
              // ✅ ส่ง sku_tag_ids: แปลงจาก form field sku_channel_tag_id (radio "ประเภทการขาย") เป็น array ของ tag_id
              //   - sku_channel_tag_id อาจอยู่ที่ item หรือ option (ตามชั้นที่ AntD Form.List เก็บไว้)
              //   - ถ้าไม่ได้เลือก (undefined) ส่ง [] เพื่อล้าง tag กลุ่ม sale_channel เดิมออก (ฝั่ง API จะ replace ทั้งหมด)
              sku_tag_ids: (item.sku_channel_tag_id ?? option.sku_channel_tag_id)
                ? [item.sku_channel_tag_id ?? option.sku_channel_tag_id]
                : [],
            }],
            id: item.id || uuidv4(),
          };
        });

        // สร้างข้อมูลราคาจาก form values - เก็บข้อมูลทั้งแบบมี base_ และไม่มี base_
        // ✅ บาง flow/เวอร์ชันของฟอร์มอาจเก็บค่าบางส่วนไว้ที่ item.items[0]
        // เพื่อความ robust ให้ fallback ไปที่ชั้นนั้น (รวมถึง api_transport_price)
        const firstSkuItemForPrice = values.sku_items[0]?.items?.[0] ?? values.sku_items[0];

        // ✅ ใช้ sellThroughLine จาก Redux state ที่ดึงไว้แล้ว
        // ถ้า sell_through_line = false ให้ส่ง vip_price และ wholesale_price เป็น 0

        const newPrice: VariationPrice = {
          variant_1_item_id: skuItemId[0]?.items?.[0]?.id || "",
          variant_2_item_id: "",
          // ราคาแบบไม่มี base_ (สำหรับแสดงผล)
          factory_price: parsePrice(firstSkuItemForPrice?.factory_price),
          // ✅ ส่ง wholesale_price และ vip_price เป็น 0 หรือ null เมื่อ sell_through_line = false
          wholesale_price: sellThroughLine ? parsePrice(firstSkuItemForPrice?.wholesale_price) : 0,
          retail_price: parsePrice(firstSkuItemForPrice?.retail_price),
          vip_price: sellThroughLine ? parsePrice(firstSkuItemForPrice?.vip_price) : 0,
          // ราคาแบบมี base_ (ข้อมูลต้นฉบับจาก form)
          base_factory_price: parsePrice(firstSkuItemForPrice?.base_factory_price),
          // ✅ ส่ง base_wholesale_price และ base_vip_price เป็น 0 หรือ null เมื่อ sell_through_line = false
          base_wholesale_price: sellThroughLine ? parsePrice(firstSkuItemForPrice?.base_wholesale_price) : 0,
          base_retail_price: parsePrice(firstSkuItemForPrice?.base_retail_price),
          base_vip_price: sellThroughLine ? parsePrice(firstSkuItemForPrice?.base_vip_price) : 0,
          // ✅ Payload Sanitation: ถ้าเป็น "self" ให้ set field ที่เกี่ยวกับแบ่งเบาเป็น null/0 (ส่ง null ใน JSON เพื่อให้ backend เคลียร์คอลัมน์ได้)
          transport_price: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? 0 : parsePrice(firstSkuItemForPrice?.transport_price),
          api_transport_carrier_code: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? null : (firstSkuItemForPrice?.api_transport_carrier_code || undefined),
          api_transport_logo: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? null : (firstSkuItemForPrice?.api_transport_logo ?? null),
          api_transport_price: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? 0 : parsePrice(firstSkuItemForPrice?.api_transport_price),
          add_on_api_transport_price: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? 0 : parsePrice(firstSkuItemForPrice?.add_on_api_transport_price),
          self_transport_price: (firstSkuItemForPrice?.shipping_method || "self") === "self" ? parsePrice(firstSkuItemForPrice?.self_transport_price) : 0,
          include_transport_price: (firstSkuItemForPrice as any)?.include_transport_price !== false,
          self_transport_carrier_id: (firstSkuItemForPrice as any)?.self_transport_carrier_id ?? null,
          // ✅ ใช้ payment_gateway_fee จาก Redux แทน hardcode (ถ้าไม่มีค่าใช้ 3 เป็น default)
          percent_payment_gateway: paymentGatewayFee !== null && paymentGatewayFee !== undefined ? paymentGatewayFee : 3,
          // Payment Gateway และคอมมิชชั่น
          payment_getway: parsePrice(firstSkuItemForPrice?.payment_getway), // ✅ เพิ่ม: ค่า Payment Gateway
          commistion_lv1: parsePrice(firstSkuItemForPrice?.commistion_lv1), // ✅ เพิ่ม: คอมมิชชั่น Level 1
          commistion_lv2: parsePrice(firstSkuItemForPrice?.commistion_lv2), // ✅ เพิ่ม: คอมมิชชั่น Level 2
          commistion_lv3: parsePrice(firstSkuItemForPrice?.commistion_lv3), // ✅ เพิ่ม: คอมมิชชั่น Level 3
          commistion_lv4: parsePrice(firstSkuItemForPrice?.commistion_lv4), // ✅ เพิ่ม: คอมมิชชั่น Level 4
          commistion_lv5: parsePrice(firstSkuItemForPrice?.commistion_lv5), // ✅ เพิ่ม: คอมมิชชั่น Level 5
          total_commission: parsePrice(firstSkuItemForPrice?.total_commission), // ✅ เพิ่ม: ค่าคอมมิชชั่นและค่าดำเนินการ

          // ราคาต่อหน่วย 4 ประเภท
          unit_factory_price: firstSkuItemForPrice?.unit_factory_price || "0", // ✅ เพิ่ม: ราคาตัวแทนต่อหน่วย
          // ✅ ส่ง unit_wholesale_price และ unit_vip_price เป็น "0" เมื่อ sell_through_line = false
          unit_wholesale_price: sellThroughLine ? (firstSkuItemForPrice?.unit_wholesale_price || "0") : "0",
          unit_retail_price: firstSkuItemForPrice?.unit_retail_price || "0", // ✅ เพิ่ม: ราคาตลาดต่อหน่วย
          unit_vip_price: sellThroughLine ? (firstSkuItemForPrice?.unit_vip_price || "0") : "0",
          quantity_per_unit: Math.max(0, parseInt(String(firstSkuItemForPrice?.quantity_per_unit ?? "0").replace(/\D/g, ""), 10) || 0), // ✅ เพิ่ม: จำนวนชิ้นต่อหน่วย
          product_unit: firstSkuItemForPrice?.product_unit || null, // ✅ เพิ่ม: หน่วยสินค้า
          id: editData?.price?.id || uuidv4(),
        };

        // ✅ Refactored: เรียก API ผ่านฟังก์ชันเดียว (เดิมมี 2 ก้อนซ้ำกัน)
        // เรียก API เฉพาะในโหมด Update และมี productId
        if (isEditMode && productId) {
          try {
            setIsSubmitting(true);
            // ส่งข้อมูลคลังไปด้วยเสมอเมื่อแท็บคลังโหลดข้อมูลแล้ว — ไม่ว่าจะกด "อัปเดท" (ราคา) หรือ "บันทึกคลัง"
            // กันเคส: ใส่ของที่คลัง → เด้งไปกรอกราคา → กดอัปเดท แล้วของคลังไม่ถูกบันทึก
            await callUpdateProductSkuApi(skuItemId, newPrice, {
              includeLinkedStock: linkedLoaded,
            });
          } catch (error: any) {
            // dependents case: callUpdateProductSkuApi เด้ง modal แล้ว → ไม่ต้องแสดงซ้ำ
            if (error?.__linkedStockHandled) {
              setIsSubmitting(false);
              return;
            }
            console.error('Error updating SKU:', error);
            message.error(error?.description || error.message || 'เกิดข้อผิดพลาดในการบันทึก SKU');
            setIsSubmitting(false);
            return;
          }
        }

        /** หน้าเพิ่มสินค้า: SKU ใหม่ที่เพิ่งเพิ่มลงหน้า (ใช้สลับ modal ไปแก้ตัวนี้) */
        let createdLocal: { variant: Variant; price: Price; index: number } | null = null;
        // อัปเดต local state เฉพาะโหมดสร้างสินค้า (ไม่ทำในโหมดอัปเดตสินค้า - ใช้ refetch แทน)
        if (!isEditMode) {
          // ใส่สถานะจัดการสต็อก (pending จาก toggle คลัง) ลง local variant ตอน "บันทึก" เท่านั้น
          const skuVariantForLocal = skuItemId[0]
            ? {
                ...skuItemId[0],
                items: (skuItemId[0].items ?? []).map((it: any, i: number) =>
                  i === 0 ? { ...it, ...buildLocalStockFields() } : it
                ),
              }
            : skuItemId[0];
          if (editMode && editData) {
            // โหมดแก้ไข - อัปเดตข้อมูลที่มีอยู่
            const oldVariantId = editData.variant.id; // ID ของ variant เดิม
            const oldItemId = editData.variant.items[0]?.id; // ID ของ item เดิม (สำหรับ match กับ price)

            // ✅ ใช้ ID ในการ match แทน index
            const updatedVariants = variants.map(v =>
              v.id === oldVariantId ? skuVariantForLocal : v
            );
            const updatedPrices = prices.map(p =>
              p.variant_1_item_id === oldItemId ? newPrice : p
            );

            // ✅ ตรวจสอบว่า variants และ prices ยังคง sync กัน
            const itemsWithoutPrice = updatedVariants.filter(v => {
              const itemId = v.items[0]?.id;
              return !updatedPrices.find(p => p.variant_1_item_id === itemId);
            });

            if (itemsWithoutPrice.length > 0) {
              // เงียบไว้เพื่อไม่ให้มี log debug ใน production
            }

            setVariants(updatedVariants);
            setPrices(updatedPrices);
          } else {
            // โหมดสร้างใหม่หรือคัดลอก - เพิ่มข้อมูลใหม่
            if (skuItemId[0]) {
              const updatedVariants = [...variants, skuVariantForLocal];
              const updatedPrices = [...prices, newPrice];
              createdLocal = { variant: skuVariantForLocal, price: newPrice as Price, index: variants.length };

              setVariants(updatedVariants);
              setPrices(updatedPrices);
            }
          }
        }

        if (modalTab === "warehouse") {
          queryClient.invalidateQueries({ queryKey: ["productLinkedStock"] });
          // คีย์จริงของ stock picker คือ 'stockPicker' (เดิม 'productStockPicker' ไม่ตรง)
          queryClient.invalidateQueries({ queryKey: ["stockPicker"] });
          setLinkedReloadToken((t) => t + 1);
          stockEditCtx?.closeStockEdit();
          stockEditCtx?.closeIngredientPicker();

          // "บันทึก" คง modal ไว้ / "บันทึกและปิด" ปิด modal
          if (closeAfterRef.current || (!editMode && !onSkuCreated)) {
            setCurrentSkuData({ variant: null, price: null });
            form.resetFields();
            onClose();
          } else if (!editMode) {
            // SKU ใหม่: สลับ modal ไปแก้ SKU ที่เพิ่งสร้าง (ถ้าคงโหมดสร้างไว้ กดบันทึกอีกจะได้ SKU ซ้ำ)
            if (createdLocal) onSkuCreated?.(createdLocal.variant, createdLocal.price, createdLocal.index);
            else setPendingSkuIds(new Set(variants.map((v) => v.items?.[0]?.id ?? "")));
          }
        } else {
          // บันทึกราคา — reset form แล้วปิด modal
          setCurrentSkuData({ variant: null, price: null });
          form.resetFields();
          onClose();
        }
      }

    } catch (error) {
      // แสดงข้อความ error ให้ผู้ใช้ทราบ
      alert("เกิดข้อผิดพลาดในการบันทึก SKU กรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง");
    } finally {
      setIsSubmitting(false);
    }
  };

  //convert sku VariationData to skuPrice
  const convertSkuItemToSkuPrice = (
    skuItems: VariationData[],
    pricesData?: PricesInput
  ) => {
    const skuPrice: VariationPrice[] = [];

    if (pricesData) {
      skuItems.forEach((item1) => {
        item1.items.forEach((item2) => {
          const price = pricesData.find((p) => {
            return p.variant_1_item_id === item2.id;
          });
          skuPrice.push({
            //   name: `${item1.name}: ${item2.name}`,// ยกเลิกการใช้ชื่อที่มี label ตัวเลือก lv1
            name: `${item2.name}`,
            variant_1_item_id: item2.id,
            variant_2_item_id: "",
            factory_price: price?.factory_price || 0,
            wholesale_price: price?.wholesale_price || 0,
            retail_price: price?.retail_price || 0,
            // ✅ เปิด edit ครั้งแรก: init input_market_price = retail_price จาก DB (เดิม init เป็น 0 — เปลี่ยนตาม requirement ใหม่)
            input_market_price: price?.retail_price || 0,
            vip_price: price?.vip_price || 0,
            base_factory_price: price?.base_factory_price || 0,
            base_wholesale_price: price?.base_wholesale_price || 0,
            base_retail_price: price?.base_retail_price || 0,
            base_vip_price: price?.base_vip_price || 0,
            transport_price: price?.transport_price || 0,
            api_transport_carrier_code: price?.api_transport_carrier_code || undefined,
            api_transport_logo: price?.api_transport_logo || undefined,
            api_transport_price: price?.api_transport_price || 0,
            add_on_api_transport_price: price?.add_on_api_transport_price || 0,
            percent_payment_gateway: price?.percent_payment_gateway || 0,
            quantity_per_unit: price?.quantity_per_unit ?? undefined,
            product_unit: price?.product_unit ?? undefined,
            self_transport_price: price?.self_transport_price ?? 0,
            include_transport_price: price?.include_transport_price !== false,
            self_transport_carrier_id: price?.self_transport_carrier_id ?? null,
          });
        });
      });
    } else {
      // 1. สร้าง skuPrice จาก skuItems แบบ 2 ระดับ
      if (skuItems.length === 2) {
        skuItems[0].items.forEach((item1) => {
          skuItems[1].items.forEach((item2) => {
            skuPrice.push({
              name: `${skuItems[0].name}: ${item1.name} ${skuItems[1].name}: ${item2.name}`,
              variant_1_item_id: item1.id,
              variant_2_item_id: item2?.id || "",
              factory_price: 0,
              wholesale_price: 0,
              retail_price: 0,
              input_market_price: 0, // ✅ ราคาตลาดด้านบน (ใช้คำนวณ)
              vip_price: 0,
              base_factory_price: 0,
              base_wholesale_price: 0,
              base_retail_price: 0,
              base_vip_price: 0,
              transport_price: 0,
              api_transport_carrier_code: undefined,
              api_transport_logo: undefined,
              api_transport_price: 0,
              add_on_api_transport_price: 0,
              percent_payment_gateway: 0,
              quantity_per_unit: 0,
            });
          });
        });
      } else if (skuItems.length === 1) {
        skuItems[0].items.forEach((item1) => {
          skuPrice.push({
            name: `${skuItems[0].name}: ${item1.name}`,
            variant_1_item_id: item1.id,
            variant_2_item_id: "",
            factory_price: 0,
            wholesale_price: 0,
            retail_price: 0,
            input_market_price: 0, // ✅ ราคาตลาดด้านบน (ใช้คำนวณ)
            vip_price: 0,
            base_factory_price: 0,
            base_wholesale_price: 0,
            base_retail_price: 0,
            base_vip_price: 0,
            transport_price: 0,
            api_transport_carrier_code: undefined,
            api_transport_logo: undefined,
            api_transport_price: 0,
            add_on_api_transport_price: 0,
            percent_payment_gateway: 0,
            quantity_per_unit: 0,
          });
        });
      }
    }

    return skuPrice;
  };

  /**
   * ฟังก์ชันสำหรับรีเซ็ต SKU Price (ไม่ใช้แล้วใน single-step)
   */
  const onResetSkuPrice = () => {
    setCurrentSkuData({ variant: null, price: null });
    form.resetFields();
  };

  /**
   * ฟังก์ชันสำหรับตรวจสอบลำดับราคา
   * เงื่อนไข: factory_price ≤ vip_price ≤ wholesale_price ≤ retail_price
   * รองรับทั้งแบบมี base_ และไม่มี base_
   */
  const validatePriceOrder = (pricesData: any) => {
    // ✅ ลบเงื่อนไขการเปรียบเทียบราคาออกแล้ว
    // ไม่มีการตรวจสอบการเปรียบเทียบราคาอีกต่อไป
    // ตรวจสอบเฉพาะว่า base_factory_price มีค่าหรือไม่ (ถ้าไม่ใช่บันทึกร่าง)
    if (isDraftSave) {
      return true; // ถ้าบันทึกร่าง ไม่ต้องตรวจสอบ
    }
    // ตรวจสอบเฉพาะ base_factory_price ว่ามีค่าและมากกว่า 0
    return pricesData.base_factory_price && pricesData.base_factory_price > 0;
  };

  /**
   * ฟังก์ชันสำหรับเลื่อนหน้าจอไปที่ฟิลด์ "ราคาที่โอนให้ผู้ประกอบการ (รวม Vat แล้ว):"
   * และทำให้ปุ่มค้นหาบริษัทขนส่งกระพริบ
   */
  const scrollToFactoryPrice = useCallback(() => {
    const factoryPriceElement = document.getElementById('factory-price-field');
    if (factoryPriceElement) {
      factoryPriceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // ✅ เริ่มการกระพริบของปุ่ม
      setShouldBlinkButton(true);
      // ✅ หยุดการกระพริบหลังจาก 3 วินาที
      setTimeout(() => {
        setShouldBlinkButton(false);
      }, 3000);
    }
  }, []);

  const isSkuSaved = editMode && !!editData;

  /** โหมดขั้นสูง + เปิดจำกัดสต็อก: ต้องมีไอเท็มที่ผูกไว้ (ไม่นับสต็อก SKU ของตัวเอง) อย่างน้อย 1 รายการ ถึงจะบันทึกได้ */
  const advancedLinkedItemCount = linkedDraftRows.filter(
    (r) => !(r.source_kind === "sku" && r.ref_stock_sku_id === skuItemIdForLinked)
  ).length;
  const advancedNeedsLinkedItem =
    modalTab === "warehouse" &&
    warehouseIsStock &&
    linkedUiMode === "advanced" &&
    advancedLinkedItemCount === 0;

  /** สต็อกหลัก (จัดการสต็อกแยก) ถูกสร้างแล้ว — หน้าแก้ไข: แถวหลักมี linked_stock_id / หน้าเพิ่มสินค้า: เก็บลง SKU ในหน้าแล้ว */
  const mainStockCreated =
    isSkuSaved &&
    (isEditMode
      ? linkedDraftRows.some((r) => isSelfSkuRow(r) && !!r.linked_stock_id)
      : storedLocalDraft?.mode === "basic");
  /**
   * เปิดติดตามสต็อก + จัดการสต็อกแยก แต่ยังไม่มีสต็อกหลัก (รวม SKU ใหม่/คัดลอก)
   * → ปิดปุ่มบันทึก ต้องกด "สร้างสต็อกหลัก" ในการ์ดก่อน
   */
  const needsMainStock =
    modalTab === "warehouse" &&
    warehouseIsStock &&
    linkedUiMode === "basic" &&
    !mainStockCreated;

  // หน้าแก้ไข: สร้าง SKU ใหม่แล้ว → รอ refetch สินค้า แล้วสลับ modal ไปแก้ SKU ตัวที่เพิ่มขึ้นมา
  useEffect(() => {
    if (!pendingSkuIds) return;
    const index = variants.findIndex((v) => !pendingSkuIds.has(v.items?.[0]?.id ?? ""));
    if (index === -1) return;
    const variant = variants[index];
    const price = prices.find((p) => p.variant_1_item_id === variant.items[0]?.id);
    setPendingSkuIds(null);
    if (price) onSkuCreated?.(variant, price, index);
    else onClose();
  }, [pendingSkuIds, variants, prices, onSkuCreated, onClose]);

  /** กำลังบันทึก หรือรอ SKU ที่เพิ่งสร้างโผล่ใน variants (กันกดบันทึกซ้ำแล้วได้ SKU ซ้ำ) */
  const isBusy = isSubmitting || !!pendingSkuIds;

  /** กดอัปเดต/บันทึก — แยกตามแท็บ: ราคา = sku/update, คลัง = linked-stock/sync */
  const handlePrimarySubmit = useCallback(async (closeAfter = false) => {
    closeAfterRef.current = closeAfter;
    if (modalTab === "warehouse") {
      if (isEditMode && !productId) {
        message.error("ไม่พบสินค้า กรุณาโหลดหน้าใหม่อีกครั้ง");
        return;
      }

      if (warehouseIsStock && linkedUiMode === "advanced") {
        // โหมดขั้นสูง: ตรวจเฉพาะ row ที่จะถูกส่ง (ไม่นับ self sku row)
        const rowsToValidate = linkedDraftRows.filter(
          (r) => !(r.source_kind === "sku" && r.ref_stock_sku_id === skuItemIdForLinked)
        );
        if (rowsToValidate.length === 0) {
          message.warning(
            `โหมด${STOCK_MODE_ADVANCED_LABEL} ต้องมีไอเท็มที่ผูกไว้อย่างน้อย 1 รายการก่อนบันทึก`
          );
          return;
        }
        const invalidQty = rowsToValidate.find((r) => !r.quantity || r.quantity <= 0);
        if (invalidQty) {
          message.warning("กรุณาระบุจำนวนที่ใช้ของแต่ละรายการให้มากกว่า 0");
          return;
        }
      }

      // SKU ที่บันทึกแล้ว: ไม่ผ่าน form.submit() เพราะ AntD จะ validate required ของแท็บราคาก่อน
      // → เรียก onFinish ตรง ๆ ด้วยค่าที่มีอยู่ในฟอร์ม (ราคาคงเดิม, บันทึกเฉพาะคลัง)
      if (isSkuSaved && !isEditMode) {
        // หน้าเพิ่มสินค้า + SKU ที่อยู่ในหน้าแล้ว: ต่อค่าคลังเข้า SKU เดิมตรง ๆ
        // ไม่สร้าง SKU/ราคาใหม่จากฟอร์ม (กันค่าราคา/ขนส่งที่ไม่ได้อยู่ในฟอร์มหาย)
        const stockFields = buildLocalStockFields();
        setVariants(
          variants.map((v) =>
            v.items?.[0]?.id === skuItemIdForLinked
              ? { ...v, items: v.items.map((it, i) => (i === 0 ? { ...it, ...stockFields } : it)) }
              : v
          )
        );
        message.success("ตั้งค่าสต็อกแล้ว — จะบันทึกเข้าระบบเมื่อกด “สร้างสินค้า”");
        if (closeAfter) onClose();
        return;
      }
      if (isSkuSaved) {
        setIsDraftSave(false);
        onFinish(form.getFieldsValue(true), true);
        return;
      }
      // SKU ใหม่/คัดลอก (ยังไม่มีในระบบ): ข้ามการตรวจราคาไม่ได้ ไม่งั้นได้ SKU ไม่มีราคา
      // → ตรวจแท็บราคาเหมือนปุ่มบันทึกปกติ แล้วบันทึกราคา+คลังพร้อมกัน (includeLinkedStock)
    }

    setIsDraftSave(false);
    try {
      await form.validateFields();
    } catch {
      switchToPriceTab();
      message.error("กรุณาตรวจสอบการกรอกข้อมูลให้ครบถ้วน");
      return;
    }

    const formValues = form.getFieldsValue(true)?.sku_items?.[0];
    if (!formValues) {
      switchToPriceTab();
      message.error("ไม่พบข้อมูล SKU — กรุณากรอกแท็บราคา");
      return;
    }

    if (formValues.shipping_method === "bangbow") {
      // ✅ ต้องจับ null ด้วย — หลัง clear COD/สลับ self ระบบมัก set เป็น null (ไม่ใช่แค่ undefined/"")
      if (!formValues.api_transport_carrier_code) {
        switchToPriceTab();
        message.error("กรุณาเลือกบริษัทขนส่ง");
        scrollToFactoryPrice();
        return;
      }
    }

    form.submit();
  }, [
    modalTab,
    isEditMode,
    productId,
    isSkuSaved,
    variants,
    setVariants,
    onClose,
    buildLocalStockFields,
    warehouseIsStock,
    linkedDraftRows,
    linkedUiMode,
    skuItemIdForLinked,
    form,
    scrollToFactoryPrice,
    onFinish, // ต้องอยู่ใน deps ไม่งั้นแท็บคลังจะเรียก onFinish ตัวเก่า (linkedLoaded ค้างเป็น false → ไม่ส่งข้อมูลคลัง)
  ]);


  /**
   * useEffect สำหรับจัดการโหมดต่างๆ เมื่อ component mount
   */
  useEffect(() => {

    if (copyMode && copyData) {
      handleCopyMode();
    } else if (editMode && editData) {
      handleEditMode();
    } else {
      handleNewMode();
    }
  }, [copyMode, editMode]); // ลบ copyData และ editData ออกจาก dependency เพื่อป้องกันการเรียกซ้ำ

  /**
   * useEffect แยกสำหรับจัดการ copyData และ editData
   * ทำงานเมื่อ copyData หรือ editData เปลี่ยนแปลง
   */
  useEffect(() => {
    if (copyMode && copyData) {
      handleCopyMode();
    } else if (editMode && editData) {
      handleEditMode();
    }
  }, [copyData, editData]); // ใช้ dependency แยกเพื่อจัดการข้อมูลที่เปลี่ยนแปลง

  /**
   * useEffect สำหรับจัดการข้อมูลเมื่อ currentSkuData เปลี่ยนแปลง
   * แปลงข้อมูลจาก variant และ price มาเป็นโครงสร้างที่ form ต้องการ
   */
  useEffect(() => {
    if (currentSkuData.variant && currentSkuData.price) {

      // ดึงข้อมูล item แรกจาก variant (เพราะ SKU มีแค่ 1 item)
      const firstItem = currentSkuData.variant.items[0];

      /**
       * แปลงข้อมูลสำหรับ form
       * โครงสร้าง form: sku_items[0] มีฟิลด์ทั้งหมดอยู่ในระดับเดียวกัน
       * ไม่ใช่ sku_items[0].items[0]
       */
      const formSkuItems = [{
        // ข้อมูลพื้นฐานจาก variant
        id: currentSkuData.variant.id,
        name: firstItem.name,
        product_code: firstItem.product_code || "",

        // รูปภาพ
        image_id: firstItem.image_id ? [
          {
            id: firstItem.image_id,
            url: `${backendUrl}images/${firstItem.image_id}`,
            name: firstItem.name,
          },
        ] : [],

        // ขนาดและน้ำหนัก
        width: firstItem.width || 0,
        length: firstItem.length || 0,
        height: firstItem.height || 0,
        weight: firstItem.weight || 0,
        packaging_type_id: firstItem.packaging_type_id || null,

        // วิธีการจัดส่ง
        shipping_method: firstItem.shipping_method || "self", // ✅ ตั้งค่า default เป็น "ส่งเอง"

        // ✅ merchants และ merchant_id: ถ้ามีอยู่แล้วใช้ค่าเดิม มิฉะนั้นใช้ default เมื่อ bangbow (ส่ง string[] เท่านั้น)
        merchants:
          (firstItem as any).merchants && (firstItem as any).merchants.length > 0
            ? toMerchantIdArray((firstItem as any).merchants)
            : (firstItem.shipping_method === "bangbow" ? getDefaultMerchantCodes() : []),
        merchant_id:
          (firstItem as any).merchant_id ||
          (firstItem.shipping_method === "bangbow"
            ? factoryData?.factory?.merchants?.speed?.find((m: { is_default: boolean }) => m.is_default)?.merchant_id ?? ""
            : ""),

        // ข้อมูลอื่นๆ
        product_capacity: firstItem.product_capacity || 0,
        product_quantity: firstItem.product_quantity || 0,
        lead_time: firstItem.lead_time || 0,
        preparation_time: firstItem.preparation_time || 1,
        is_active: typeof firstItem.is_active === "boolean" ? firstItem.is_active : true,
        is_draft: firstItem.is_draft !== undefined ? firstItem.is_draft : false,
        is_cod: firstItem.is_cod ?? false,

        // ราคาตั้งต้น (base prices)
        base_factory_price: currentSkuData.price?.base_factory_price || 0,
        base_vip_price: currentSkuData.price?.base_vip_price || 0,
        base_wholesale_price: currentSkuData.price?.base_wholesale_price || 0,
        base_retail_price: currentSkuData.price?.base_retail_price || 0,

        // ค่าขนส่ง
        transport_price: currentSkuData.price?.transport_price || 0,
        // ✅ ขนส่งจาก API ที่เลือก (เพื่อให้ reopen modal แล้วเลือก/โลโก้ตรงกับค่าล่าสุด)
        api_transport_carrier_code: currentSkuData.price?.api_transport_carrier_code || undefined,
        api_transport_logo: currentSkuData.price?.api_transport_logo || undefined,
        api_transport_price: currentSkuData.price?.api_transport_price || 0,
        add_on_api_transport_price: currentSkuData.price?.add_on_api_transport_price || 0,
        // ✅ [requirement 2026-10-16] ไม่ fallback เป็น 0 — ถ้ายังไม่เคยมีค่า (สร้างใหม่) ให้คงเป็น undefined (ว่าง)
        self_transport_price: (currentSkuData.price as any)?.self_transport_price,
        include_transport_price: (currentSkuData.price as any)?.include_transport_price !== false,
        self_transport_carrier_id: (currentSkuData.price as any)?.self_transport_carrier_id ?? null,
        partner_id: (firstItem as any).partner_transport_id || "",
        brand_id: (firstItem as any).partner_transport_brand_id || "",

        // ราคาสุดท้าย (final prices)
        factory_price: currentSkuData.price?.factory_price || 0,
        vip_price: currentSkuData.price?.vip_price || 0,
        wholesale_price: currentSkuData.price?.wholesale_price || 0,
        retail_price: currentSkuData.price?.retail_price || 0,
        // ✅ เปิด edit/copy ครั้งแรก: init input_market_price = retail_price จาก DB (เดิม init เป็น 0 — เปลี่ยนตาม requirement ใหม่)
        input_market_price: currentSkuData.price?.retail_price || 0,

        // Payment Gateway
        percent_payment_gateway: currentSkuData.price?.percent_payment_gateway || 0,
        payment_getway: currentSkuData.price?.payment_getway || 0, // ✅ เพิ่ม: ค่า Payment Gateway

        // คอมมิชชั่น 3 ระดับ
        commistion_lv1: currentSkuData.price?.commistion_lv1 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 1
        commistion_lv2: currentSkuData.price?.commistion_lv2 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 2
        commistion_lv3: currentSkuData.price?.commistion_lv3 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 3
        commistion_lv4: currentSkuData.price?.commistion_lv4 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 4
        commistion_lv5: currentSkuData.price?.commistion_lv5 || 0, // ✅ เพิ่ม: คอมมิชชั่น Level 5
        total_commission: currentSkuData.price?.total_commission || 0, // ✅ เพิ่ม: ค่าคอมมิชชั่นและค่าดำเนินการ

        // ราคาต่อหน่วย 4 ประเภท
        unit_factory_price: currentSkuData.price?.unit_factory_price || "0", // ✅ เพิ่ม: ราคาตัวแทนต่อหน่วย
        unit_wholesale_price: currentSkuData.price?.unit_wholesale_price || "0", // ✅ เพิ่ม: ราคาขายส่งต่อหน่วย
        unit_retail_price: currentSkuData.price?.unit_retail_price || "0", // ✅ เพิ่ม: ราคาตลาดต่อหน่วย
        unit_vip_price: currentSkuData.price?.unit_vip_price || "0", // ✅ เพิ่ม: ราคา VIP ต่อหน่วย
        quantity_per_unit: currentSkuData.price?.quantity_per_unit ?? undefined, // ✅ เพิ่ม: จำนวนชิ้นต่อหน่วย
        product_unit: currentSkuData.price?.product_unit ?? undefined, // ✅ เพิ่ม: หน่วยสินค้า
        // ✅ ประเภทการขาย (sale_channel) — pick tag id แรกที่ exclusive_group=sale_channel จาก price.sku_tags
        sku_channel_tag_id:
          (currentSkuData.price as any)?.sku_tags?.find?.(
            (t: any) => t?.exclusive_group === "sale_channel"
          )?.id ?? undefined,
      }];

      // ตั้งค่า form values
      form.setFieldsValue({
        sku_items: formSkuItems,
      });

      // ✅ ตั้งค่า total_commission โดยตรงเพื่อให้แน่ใจว่าไม่ถูก override
      if (currentSkuData.price?.total_commission !== undefined && currentSkuData.price?.total_commission !== null) {
        setTimeout(() => {
          form.setFieldValue(['sku_items', 0, 'total_commission'], currentSkuData.price?.total_commission);
        }, 100);
      }

      // ✅ ตั้งค่า shipping_method + dimension + transport โดยตรงหลัง setFieldsValue
      // เพื่อป้องกัน initialValue / timing ทำให้ค่าโดน override ตอนเปิด edit/copy
      // ✅ ใช้ hasPopulatedRef เพื่อกัน packaging_type_id (ที่ user อาจเปลี่ยนแล้ว) โดน override ซ้ำเมื่อ useEffect re-fire
      const isFirstPopulation = !hasPopulatedRef.current;
      hasPopulatedRef.current = true;
      setTimeout(() => {
        form.setFieldValue(['sku_items', 0, 'shipping_method'], formSkuItems[0]?.shipping_method || 'self');
        form.setFieldValue(['sku_items', 0, 'width'], formSkuItems[0]?.width ?? 0);
        form.setFieldValue(['sku_items', 0, 'length'], formSkuItems[0]?.length ?? 0);
        form.setFieldValue(['sku_items', 0, 'height'], formSkuItems[0]?.height ?? 0);
        form.setFieldValue(['sku_items', 0, 'weight'], formSkuItems[0]?.weight ?? 0);
        // ✅ set packaging_type_id เฉพาะครั้งแรกที่ populate (initial load ของ edit/copy)
        // ถ้า re-fire ภายหลัง ห้ามทับค่าที่ user เพิ่งเปลี่ยน
        if (isFirstPopulation) {
          form.setFieldValue(['sku_items', 0, 'packaging_type_id'], formSkuItems[0]?.packaging_type_id ?? null);
          form.setFieldValue(['sku_items', 0, 'product_unit'], formSkuItems[0]?.product_unit ?? undefined);
        }
        form.setFieldValue(['sku_items', 0, 'transport_price'], formSkuItems[0]?.transport_price ?? 0);
        // ✅ sync carrier/logo ที่เลือก ให้ตรงกับค่าล่าสุดใน state ด้วย
        form.setFieldValue(['sku_items', 0, 'api_transport_carrier_code'], formSkuItems[0]?.api_transport_carrier_code);
        form.setFieldValue(['sku_items', 0, 'api_transport_logo'], formSkuItems[0]?.api_transport_logo);
        form.setFieldValue(['sku_items', 0, 'api_transport_price'], formSkuItems[0]?.api_transport_price ?? 0);
        form.setFieldValue(['sku_items', 0, 'add_on_api_transport_price'], formSkuItems[0]?.add_on_api_transport_price ?? 0);
        // ✅ [requirement 2026-10-16] ไม่ fallback เป็น 0 — ให้คงว่างถ้ายังไม่เคยกรอก (สร้างใหม่)
        form.setFieldValue(['sku_items', 0, 'self_transport_price'], formSkuItems[0]?.self_transport_price);
        form.setFieldValue(['sku_items', 0, 'partner_id'], formSkuItems[0]?.partner_id ?? "");
        form.setFieldValue(['sku_items', 0, 'brand_id'], formSkuItems[0]?.brand_id ?? "");
        form.setFieldValue(['sku_items', 0, 'is_cod'], formSkuItems[0]?.is_cod ?? false);
        form.setFieldValue(['sku_items', 0, 'merchants'], formSkuItems[0]?.merchants ?? []);
        form.setFieldValue(['sku_items', 0, 'merchant_id'], formSkuItems[0]?.merchant_id ?? "");
        // ✅ ช่องทางการขาย — set ซ้ำหลัง setFieldsValue เผื่อ master_sku_tag options ยังไม่ load เสร็จ + กัน timing race กับ Radio.Group
        form.setFieldValue(['sku_items', 0, 'sku_channel_tag_id'], formSkuItems[0]?.sku_channel_tag_id ?? undefined);
      }, 0);

      // ตั้งค่า formValidate เป็น true เมื่อมีข้อมูลแล้ว
      setFormValidate(true);
    }
  }, [currentSkuData, form]);

  return (
    <>
      {/* Single-Step Form - รวมทั้งการกำหนด variants และ prices */}
      <Form 
        form={form} 
        onFinish={onFinish}
        onFinishFailed={() => {
          switchToPriceTab();
          message.error('กรุณาตรวจสอบการกรอกข้อมูลให้ครบถ้วน');
        }}
        className="form-variation"
      >
        {!warehouseOnly && (
          <TabButtonRow>
            <Segmented
              value={modalTab}
              style={{ fontWeight: 500 }}
              onChange={(v) => {
                const tab = v as 'price' | 'warehouse';
                if (tab === 'price') {
                  switchToPriceTab();
                } else {
                  stockEditCtx?.setModalLayoutTab(tab);
                  setModalTab(tab);
                }
              }}
              options={[
                { value: "price", label: "ราคาและการขาย" },
                { value: "warehouse", label: "สต็อกสินค้า" },
              ]}
            />
          </TabButtonRow>
        )}

        {/* keep both mounted; just toggle visibility */}
        <TabPanels>
          <TabPanel $active={modalTab === "price"}>
            <Wrapper>
              <FormWrapper>
                <VariationOptionCard
                  form={form}
                  skuItems={currentSkuData.variant ? [currentSkuData.variant] : []}
                  variants={variants}
                  skuImage={skuImage}
                  setSkuImage={setSkuImage}
                  editMode={editMode}
                  copyMode={copyMode}
                  isDraftSave={isDraftSave}
                  editData={editData}
                  originalPrices={originalPrices}
                  originalVariants={originalVariants}
                  speedHasSearched={speedHasSearched}
                  setSpeedHasSearched={setSpeedHasSearched}
                  shouldBlinkButton={shouldBlinkButton}
                  setShouldBlinkButton={setShouldBlinkButton}
                  factoryId={factoryId}
                  percentCommission={percentCommission}
                  categoryName={categoryName}
                />
              </FormWrapper>
            </Wrapper>
          </TabPanel>

          <TabPanel $active={modalTab === "warehouse"}>
            <SkuModalLinkedStockTab
              skuItemId={skuItemIdForLinked}
              productId={productId}
              skuName={skuNameForWarehouse}
              skuImageUrl={skuImageUrlForWarehouse}
              isStock={linkedSkuItem?.is_stock ?? false}
              stock={linkedSkuItem?.stock ?? null}
              isEditMode={isEditMode}
              // หน้าเพิ่มสินค้า: SKU ที่ "บันทึก" แล้วอยู่แค่ใน state ของหน้า ยังไม่มีใน DB → ห้ามยิง API ของแท็บ
              isSkuSaved={isEditMode && editMode && !!editData}
              draftRows={linkedDraftRows}
              onDraftRowsChange={setLinkedDraftRows}
              onStockToggle={handleModalStockToggle}
              onStockToggled={onStockToggled}
              onWarehouseStockChange={setWarehouseIsStock}
              linkedReloadToken={linkedReloadToken}
              onLinkedUiModeChange={setLinkedUiMode}
              onLinkedLoaded={setLinkedLoaded}
              localDraft={localStockDraft}
              localStockOptions={localStockOptions}
              initialQuantity={isSkuInDb ? undefined : initialStockQty}
              onInitialQuantityChange={isSkuInDb ? undefined : setInitialStockQty}
              mainStockSaved={mainStockCreated}
              onCreateMainStock={needsMainStock ? () => handlePrimarySubmit() : undefined}
              creatingMainStock={isBusy}
            />
          </TabPanel>
        </TabPanels>

        {/* {!speedHasSearched && (
          <div style={{ marginTop: 16, marginBottom: 16, width: '100%', cursor: 'pointer' }} onClick={scrollToFactoryPrice}>
            <Alert type="warning" showIcon message='กดปุ่ม "ค้นหาบริษัทขนส่ง" เพื่อบันทึกการกำหนดราคาและลักษณะแพ็คเกจสินค้า' />
          </div>
        )} */}

        {/* คำอธิบายสถานะคลัง — แสดงเฉพาะแท็บคลัง ให้รู้ว่า "บันทึกแล้ว/รอบันทึก" คืออะไร */}
        {modalTab === "warehouse" && isEditMode && !needsMainStock && (
          <WarehouseSaveHint>
            <div>
              <Tag color="green" style={{ marginInlineEnd: 6 }}>บันทึกแล้ว</Tag>
              ข้อมูลล่าสุดถูกบันทึกเรียบร้อยแล้ว สามารถปรับจำนวนและตรวจสอบสต็อกพร้อมขายได้
            </div>
            <div style={{ marginTop: 6 }}>
              <Tag color="orange" style={{ marginInlineEnd: 6 }}>ยังไม่ได้บันทึก</Tag>
              มีข้อมูลที่เพิ่มหรือแก้ไขแต่ยังไม่ได้บันทึก กรุณากด <b>“บันทึกสต็อก”</b> เพื่อยืนยันข้อมูล
            </div>
          </WarehouseSaveHint>
        )}

        {/* ปุ่มจัดการ */}
        <ButtonWrapper>
          {/* ปุ่มยกเลิก */}
          <Button
            type="default"
            htmlType="button"
            onClick={onClose}
            style={{ width: 100, marginRight: 10 }}
          >
            ยกเลิก
          </Button>

          {/* ปุ่มบันทึกร่าง - ซ่อนถ้าแก้ไขและเผยแพร่แล้ว */}
          {!(editMode && editData && editData.variant.items[0] && editData.variant.items[0].is_draft === false) && (
            <Button
              type="default"
              htmlType="button"
              loading={isSubmitting}
              disabled={isSubmitting}
              style={{ width: 200, marginRight: 10, display: "none" }}
              onClick={() => {
                setIsDraftSave(true);
                form.submit();
              }}
            >
              บันทึกร่าง
            </Button>
          )}

          {/* ปุ่มบันทึก/อัปเดต/คัดลอก */}
          <Tooltip
            title={
              advancedNeedsLinkedItem
                ? "เพิ่มไอเท็มที่ผูกไว้อย่างน้อย 1 รายการก่อนบันทึก"
                : ""
            }
          >
            <Button
              type="primary"
              htmlType="button"
              loading={isBusy}
              disabled={isBusy || advancedNeedsLinkedItem || needsMainStock}
              style={{ width: 200 }}
              onClick={() => handlePrimarySubmit()}
            >
              {modalTab === "warehouse"
                ? isEditMode
                  ? "บันทึกสต็อก"
                  : "บันทึก" // หน้าเพิ่มสินค้า: เก็บไว้กับ SKU ก่อน บันทึกจริงตอนกดสร้างสินค้า
                : editMode
                  ? "บันทึกและปิด"
                  : copyMode
                    ? "บันทึก"
                    : "บันทึก"}
            </Button>
          </Tooltip>

          {/* แท็บคลัง: "บันทึก" คง modal ไว้ / "บันทึกและปิด" บันทึกแล้วปิด modal */}
          {modalTab === "warehouse" && !needsMainStock && (
            <Button
              type="primary"
              htmlType="button"
              loading={isBusy}
              disabled={isBusy || advancedNeedsLinkedItem}
              style={{ width: 140, marginLeft: 10 }}
              onClick={() => handlePrimarySubmit(true)}
            >
              บันทึกและปิด
            </Button>
          )}
        </ButtonWrapper>
      </Form>
    </>
  );
};

export default ProductVariationCreate;

const ButtonWrapper = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  margin-top: 16px;
  padding: 12px 0;
  background: #fff;
  border-top: 1px solid #f0f0f0;
`;

/** กล่องอธิบายสถานะคลัง (บันทึกแล้ว/ยังไม่บันทึก) เหนือปุ่มท้าย modal */
const WarehouseSaveHint = styled.div`
  margin-top: 16px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #595959;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  margin-top: 16px;
`;

const TabButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  margin-top: 8px;
`;

const TabPanels = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const TabPanel = styled.div<{ $active: boolean }>`
  display: ${(p) => (p.$active ? 'block' : 'none')};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;
